import {Fragment, useRef, useState} from "react";
import {format} from "date-fns";
import {
    BreadCrumb,
    TablePaging,
    CenteredModal,
    FormInput,
    ReactSingleSelectServer
} from "~/components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEye} from "@fortawesome/free-solid-svg-icons";
import UserPlanSubscriptionAPI from '~/api/userPlanSubscription';
import {Check, ClockPlus} from 'lucide-react';
import {Controller, useForm} from "react-hook-form";

const breadCrumbItem = [
    { text: "AI Chatbot Page", to: "/ai-chatbot/administrator", isActive: false },
    { text: "User Plan Subscription", to: "", isActive: true },
]

const formatRemainingTime = (endDate)=> {
    const now = new Date();
    const end = new Date(endDate);

    if (end <= now) return "Expired";

    let diffMs = end - now;

    const totalHours = Math.floor(diffMs / (1000 * 60 * 60));
    const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (totalDays < 1) {
        return `${totalHours} hours`;
    }

    let remainingDays = totalDays;

    const years = Math.floor(remainingDays / 365);
    remainingDays %= 365;

    const months = Math.floor(remainingDays / 30);
    remainingDays %= 30;

    const weeks = Math.floor(remainingDays / 7);
    const days = remainingDays % 7;

    let result = [];

    if (years > 0) {
        result.push(`${years} years`);
        if (months > 0) result.push(`${months} months`);
        if (days > 0) result.push(`${days} days`);
        return result.join(" ");
    }

    if (months > 0) {
        result.push(`${months} months`);
        if (days + weeks * 7 > 0) result.push(`${days + weeks * 7} days`);
        return result.join(" ");
    }

    if (weeks > 0) {
        result.push(`${weeks} weeks`);
        if (days > 0) result.push(`${days} days`);
        return result.join(" ");
    }

    return `${days} days`;
}

function  UserPlanSubscription() {
    const [reloadTable, setReloadTable] = useState(false);
    const [userPlanSubscriptionDetails, setUserPlanSubscriptionDetails] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [showPlanRenewalModal, setShowPlanRenewalModal] = useState(false);
    const [errorConfirmPassword, setErrorConfirmPassword] = useState("");
    const dateRef = useRef();

    const handleNewPlanRenewal = () => {
        reset({
                "UserPlanSubscription": {
                    "UserPlanSubscriptionId": 0,
                    "UserId": "",
                    "UserName": "",
                    "AvatarFileName": "",
                    "AvatarUrl": "",
                    "ServicePlanId": 0,
                    "ServicePlan": "",
                    "ServiceTypeId": 0,
                    "ServiceType": "",
                    "StartDate": "2025-01-01T00:00:00",
                    "EndDate": "2025-01-01T00:00:00",
                    "PaymentStatus": "Paid",
                    "CreatedAt": "2025-01-01T00:00:00",
                    "DurationValue": 0,
                    "DurationUnit": "Hour",
                    "Price": 0,
                    "Discount": 0,
                    "AmountPaid": 0
                },
                "AdminVerify": {
                    "AdminEmail": "",
                    "AdminPassword": ""
                }
            }
        );
        setShowPlanRenewalModal(true);
    }

    const tablePagingConfig = {
        columns: [
            {
                name: 'ID',
                width: '70px',
                className: 'fw-bold',
                key: 'UserPlanSubscriptionId'
            },
            {
                name: 'PAYER',
                width: '150px',
                className: 'fw-bold',
                key: 'Payer',
                template: (row) => {
                    return (
                        <div className=" d-flex align-items-center text-body" data-discover="true">
                            <div className="avatar avatar-m">
                                <img alt="avatar" className="rounded-circle" src={row.AvatarUrl}/>
                            </div>
                            <p className="mb-0 ms-3 text-body-emphasis fw-semibold">{row.UserName}</p>
                        </div>
                    )
                }
            },
            {
                name: 'SERVICE PLAN',
                width: '120px',
                key: 'ServicePlan'
            },
            {
                name: 'SERVICE TYPE',
                width: '130px',
                key: 'ServiceType'
            },
            {
                name: 'START DATE',
                width: '150px',
                key: 'StartDate',
                template: (row) => {
                    return (
                        <div className="d-flex flex-column">
                            <span>{format(new Date(row.StartDate), "dd/MM/yyyy")}</span>
                            <span>{format(new Date(row.StartDate), "hh:mm a")}</span>
                        </div>
                    )
                }
            },
            {
                name: 'END DATE',
                width: '150px',
                key: 'EndDate',
                template: (row) => {
                    return (
                        <div className="d-flex flex-column">
                            <span>{format(new Date(row.EndDate), "dd/MM/yyyy")}</span>
                            <span>{format(new Date(row.EndDate), "hh:mm a")}</span>
                        </div>
                    )
                }
            },
            {
                name: 'TIME REMAINING',
                width: '150px',
                key: 'EndDate',
                template: (row) => {
                    const timeRemaining = formatRemainingTime(row.EndDate);
                    return (
                        <span className={`badge badge-bt ${timeRemaining === "Expired" ? "badge-bt-danger" : "badge-bt-success"}`}>{timeRemaining}</span>
                    )
                }
            },
            {
                name: 'IS ACTIVE',
                width: '100px',
                key: 'IsActive',
                template: (row) => {
                    const template = row.IsActive ? <span className="badge badge-bt badge-bt-success">
                            <span className="badge-label">Active</span>
                            <Check size={12}/>
                        </span> :
                        <span className="badge bg-secondary">Inactive</span>;
                    return template;
                }
            },
            {
                name: 'PAYMENT STATUS',
                width: '150px',
                key: 'PaymentStatus'
            },
            {
                name: 'CREATED AT',
                width: '120px',
                key: 'CreatedAt',
                template: (row) => {
                    return (
                        <span>{format(new Date(row.CreatedAt), "dd-MM-yyyy")}</span>
                    )
                }
            },
            {
                name: 'DURATION VALUE',
                width: '170px',
                key: 'SubscriptionValue',
                template: (row) => {
                    return (
                        <span>{row.DurationValue}</span>
                    )
                }
            },
            {
                name: 'DURATION UNIT',
                width: '170px',
                key: 'SubscriptionUnit',
                template: (row) => {
                    return (
                        <span>{row.DurationUnit}</span>
                    )
                }
            },
            {
                name: 'PRICE',
                width: '150px',
                key: 'Price',
                template: (row) => {
                    return (
                        <span>${row.Price}</span>
                    )
                }
            },
            {
                name: 'DISCOUNT',
                width: '100px',
                key: 'Discount',
                template: (row) => {
                    return (
                        <span>{row.Discount}%</span>
                    )
                }
            },
            {
                name: 'AMOUNT PAID',
                width: '110px',
                key: 'AmountPaid',
                template: (row) => {
                    return (
                        <span>${row.AmountPaid}</span>
                    )
                }
            },
            {
                name: 'ACTION',
                width: '70px',
                key: 'Action',
                className: 'text-center',
                headerClassName: "text-center",
                template: (row) => {
                    return (
                        <span><FontAwesomeIcon onClick={()=> handleUserPlanSubscriptionDetail(row.UserPlanSubscriptionId)} icon={faEye} className="text-primary cursor-pointer"/></span>
                    )
                }
            }
        ],
        url: "/user-plan-subscriptions/paged",
        tableKey: "TableUserPlanSubscription",
        extraButtons: [
            <button
                key="newPlan"
                className="btn btn-bt-primary ms-2"
                type="button"
                onClick={handleNewPlanRenewal}
            >
                Plan Renewal <ClockPlus size={15}/>
            </button>
        ]
    }

    const detailFields = [
        {name: "ServicePlan", label: "Service Plan"},
        {name: "ServiceType", label: "Service Type"},
        {name: "StartDate", label: "Start Date"},
        {name: "EndDate", label: "End Date"},
        {name: "CanceledAt", label: "Canceled At"},
        {name: "PaymentStatus", label: "Payment Status"},
        {name: "CreatedAt", label: "Created At"},
        {name: "SubscriptionValue", label: "Subscription Value"},
        {name: "SubscriptionUnit", label: "Subscription Unit"},
        {name: "Price", label: "Price"},
        {name: "Discount", label: "Discount"},
        {name: "AmountPaid", label: "Amount Paid"},
    ]

    const handleUserPlanSubscriptionDetail = (userPlanSubscriptionId) => {
        UserPlanSubscriptionAPI.getById(userPlanSubscriptionId)
            .then((res) => {
                setUserPlanSubscriptionDetails(res.data);
                setShowModal(true);
            })
            .catch(() => {
            })
    }

    const {
        register,
        handleSubmit,
        reset,
        getValues,
        control,
        formState: { errors }
    } = useForm();

    const onSubmitForm = (data) =>{
       data.UserPlanSubscription.UserId = data.UserPlanSubscription.UserId.value;
       UserPlanSubscriptionAPI.planRenewal(data)
           .then((res) => {
                if(res.data.Succeeded === false){
                    setErrorConfirmPassword(res.data.Message);
                }
            })
    }

    return(
        <Fragment>
            <BreadCrumb breadCrumbItem={breadCrumbItem}/>
            <h2 className="text-bold text-body-emphasis mb-5 ">User Plan Subscription</h2>
            <TablePaging tablePagingConfig={tablePagingConfig} reloadTable={reloadTable}/>
            <CenteredModal
                show={showModal}
                onClose={() => setShowModal(false)}
                title="User Plan Subscription Details"
                closeText="Close"
                modalType="modal-lg"
            >
                <div className="row">
                    <div>
                        <div className=" d-flex align-items-center text-body justify-content-center flex-column" data-discover="true">
                            <div className="avatar avatar-xl">
                                <img alt="avatar" className="rounded-circle" src={userPlanSubscriptionDetails.AvatarUrl}/>
                            </div>
                            <p className="my-3 text-body-emphasis fw-semibold">{userPlanSubscriptionDetails.UserName}</p>
                        </div>
                    </div>
                    {
                        detailFields.map((item, index) => (
                            <div className="col-sm-6 col-12" key={index}>
                                <FormInput showHeaderLabel={true}
                                           name={item.name}
                                           required={false}
                                           disabled={true}
                                           value={userPlanSubscriptionDetails[item.name]}
                                           label={`Assign ${item.label}`}
                                           maxLengthText=""
                                />
                            </div>
                        ))
                    }
                </div>
            </CenteredModal>

            <CenteredModal show={showPlanRenewalModal}
                           onClose={() => {
                               setShowPlanRenewalModal(false);
                           }}
                           title="Plan Renewal"
                           formId="formPlanRenewal"
            >
                <form id="formPlanRenewal" onSubmit={handleSubmit(onSubmitForm)}>
                    <div className="fs-8-1 text-center mb-3 text-danger fw-semibold">
                        Renewing it will deactivate all other active plans linked to this account.
                    </div>

                    <div className="mb-3">
                        <Controller
                            name="UserPlanSubscription.UserId"
                            control={control}
                            rules={{required: "User is required."}}
                            render={({field}) => (
                                <ReactSingleSelectServer
                                    fetchUrl="/user-plan-subscriptions/ddl-user-plan-renewal"
                                    value={field.value}
                                    onChange={field.onChange}
                                    errors={errors}
                                    name="UserPlanSubscription.UserId"
                                    placeholder="Select User..."
                                />
                            )}
                        />
                    </div>

                    <div className="mb-3">
                        <FormInput showHeaderLabel={true}
                                   name = "UserPlanSubscription.DurationValue"
                                   errors={errors}
                                   required= {true}
                                   maxLengthText=""
                                   label="Assign Duration Value"
                                   validation={{...register('UserPlanSubscription.DurationValue', {
                                           required: 'Duration Value is required.',
                                           pattern: {
                                               value: /^[0-9]+$/,
                                               message: 'Only numbers are allowed.'
                                           },
                                           validate: value => {
                                               const num = parseInt(value, 10);
                                               if (isNaN(num)) return 'Invalid number.';
                                               if (num < 1) return 'Must be at least 1.';
                                               if (num > 99999) return 'Must 1be no more than 99999.';
                                               return true;
                                           }
                                       })}
                                   }
                        />
                    </div>

                    <div className="mb-3">
                        <div className="mb-2"><span className="fs-8-1 fw-semibold">Duration Unit</span></div>
                        <div className="form-check form-check-inline">
                            <input id="rad1"
                                   className="form-check-input"
                                   type="radio"
                                   defaultChecked
                                   value = {"Hour"}
                                   {...register("UserPlanSubscription.DurationUnit")}
                            />
                            <label title="" htmlFor="rad1" className="form-check-label">Hour</label>
                        </div>
                        <div className="form-check form-check-inline">
                            <input id="rad2"
                                   className="form-check-input"
                                   type="radio"
                                   value = {"Day"}
                                   {...register("UserPlanSubscription.DurationUnit")}
                            />
                            <label title="" htmlFor="rad2" className="form-check-label">Day</label>
                        </div>
                        <div className="form-check form-check-inline">
                            <input id="rad3"
                                   className="form-check-input"
                                   type="radio"
                                   value = {"Week"}
                                   {...register("UserPlanSubscription.DurationUnit")}
                            />
                            <label title="" htmlFor="rad3" className="form-check-label">Week</label>
                        </div>
                        <div className="form-check form-check-inline">
                            <input id="rad4"
                                   className="form-check-input"
                                   type="radio"
                                   value = {"Month"}
                                   {...register("UserPlanSubscription.DurationUnit")}
                            />
                            <label title="" htmlFor="rad4" className="form-check-label">Month</label>
                        </div>
                        <div className="form-check form-check-inline">
                            <input id="rad5"
                                   className="form-check-input"
                                   type="radio"
                                   value = {"Year"}
                                   {...register("UserPlanSubscription.DurationUnit")}
                            />
                            <label title="" htmlFor="rad5" className="form-check-label">Year</label>
                        </div>
                    </div>

                    <div className="mt-4">
                        <span className="fs-8-1 fw-bold" >Enter your password to confirm</span>
                    </div>
                    <FormInput
                        label="Verify Password"
                        name="AdminVerify.AdminPassword"
                        errors={errors}
                        required={true}
                        defaultValue=""
                        maxLengthText = ""
                        showHeaderLabel={false}
                        validation={{...register("AdminVerify.AdminPassword", {
                                required: "Admin Password is required.",
                            })}}
                    />
                    {
                        errorConfirmPassword &&
                        <p className="text-danger fs-9 fw-bold">{errorConfirmPassword}</p>
                    }
                </form>
            </CenteredModal>
        </Fragment>
    )
}
export default UserPlanSubscription;